import React from 'react';

import {
  AlgorithmStep,
  AlgorithmControlStructure
} from '../../types/algorithm';
import {
  GetTaskById,
  GetAreaById,
  GetZoneById,
  GetTimerById,
  GetTitleById,
  GetResourceValue,
  GetTaskContentById
} from '../../utils/game-document';
import Operations from '../../constants/operations';
import { GameDocumentContext } from '../../contexts/game-document';
import { GetInventoryById } from '../../utils/game-document/assets/inventories';

const plural = (n: number | undefined, word: string) =>
  `${n} ${word}${Number(n) === 1 ? '' : 's'}`;

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

type GameDoc = NonNullable<Parameters<typeof GetTitleById>[0]>;

const buildStepOperation = (step: AlgorithmStep, doc: GameDoc): string => {
  const who = step.identifier ?? '';
  const qty = step.argumentQuantity;
  const assetId = (step.argumentAssetId as string) ?? '';

  const name = (asset?: { titleResId?: string } | null) =>
    GetResourceValue(doc, asset?.titleResId ?? '');

  const nameList = (
    getter: (
      d: GameDoc,
      id: string
    ) => { titleResId?: string } | null | undefined
  ) =>
    Array.isArray(step.argumentAssetId)
      ? (step.argumentAssetId as string[])
          .map((id) => `"${name(getter(doc, id))}"`)
          .join(', ')
      : '""';

  const task = () => name(GetTaskById(doc, assetId));
  const timer = () => name(GetTimerById(doc, assetId));
  const area = () => name(GetAreaById(doc, assetId));
  const title = () => name(GetTitleById(doc, assetId));
  const item = () => name(GetInventoryById(doc, assetId));

  switch (step.operation) {
    // 1. Task actions
    case 'completeTask':
      return 'Complete the current task';
    case 'openTask':
      return `Open "${task()}" for ${who}`;
    case 'showTask':
      return `Show "${task()}" to ${who}`;
    case 'hideTask':
      return `Hide "${task()}" from ${who}`;
    case 'resetTask':
      return `Reset "${task()}" for ${who}`;
    case 'distributeTask':
      return `Distribute ${plural(qty, 'task')} to ${who}`;
    case 'randomTask':
      return `Show ${plural(qty, 'random task')} to ${who}`;
    case 'openRandomTask':
      return `Open ${plural(qty, 'random task')} for ${who}`;

    // 2. Timer actions
    case 'startTimer':
      return `Start "${timer()}" timer`;
    case 'stopTimer':
      return `Stop "${timer()}" timer`;
    case 'pauseTimer':
      return `Pause "${timer()}" timer`;
    case 'resumeTimer':
      return `Resume "${timer()}" timer`;

    // 3. Score actions
    case 'giveScore(score)':
      if (step.argumentBoolean || step.argumentBoolean === undefined)
        return `Award the accumulated score from all forms in this task to ${who}`;
      return `Award ${plural(qty, 'point')} to ${who}`;
    case 'giveScore':
      return `Award ${plural(qty, 'point')} to ${who}`;
    case 'removeScore':
      return `Deduct ${plural(qty, 'point')} from ${who}`;

    // 4. Inventory actions
    case 'giveItem':
      return `Give ${qty} "${item()}" to ${who}`;
    case 'removeItem':
      return `Remove ${qty} "${item()}" from ${who}`;
    case 'addBankStock':
      return `Increase the stock of bank item "${item()}" by ${qty}`;
    case 'removeBankStock':
      return `Decrease the stock of bank item "${item()}" by ${qty}`;

    // 5. Title actions
    case 'giveTitle':
      return `Award "${title()}" title to ${who}`;
    case 'assignTitle':
      return `Award one of the following titles to ${who}: ${nameList(GetTitleById)}`;
    case 'removeTitle':
      return `Remove "${title()}" title from ${who}`;

    // 6. Navigation actions
    case 'navigateToWorldMap':
      return `Navigate ${who} to World Map`;
    case 'navigateToZone':
      return `Navigate ${who} to "${name(GetZoneById(doc, assetId))}" Zone Map`;

    // 7. Area and game actions
    case 'showArea':
      return `Show "${area()}" area to ${who}`;
    case 'hideArea':
      return `Hide "${area()}" area from ${who}`;
    case 'endTheGame':
      return 'End the game for all players';

    default:
      return (
        Operations.find((o) => o.name === step.operation)?.description ?? ''
      );
  }
};

export const StepSummary: React.FC<{ step: AlgorithmStep }> = ({ step }) => {
  const [state] = React.useContext(GameDocumentContext);

  const summary = React.useMemo(
    () => buildStepOperation(step, state.gameDocument!),
    [
      step.operation,
      step.identifier,
      state.gameDocument,
      step.argumentAssetId,
      step.argumentBoolean,
      step.argumentQuantity
    ]
  );

  return (
    <span
      className={'collapsed-text'}
      style={{ fontSize: '20px', lineHeight: '28px' }}>
      {!step.identifier ? '' : `${summary}.`}
    </span>
  );
};

export const StructureStepSummary: React.FC<{
  step: AlgorithmControlStructure;
}> = ({ step }) => {
  const [state] = React.useContext(GameDocumentContext);

  const lines = React.useMemo(() => {
    const doc = state.gameDocument!;

    const buildAnswerPhrase = (
      argumentAssetId: unknown,
      taskId: string | undefined
    ) => {
      const empty = 'the "" answer';

      if (!Array.isArray(argumentAssetId) || argumentAssetId.length === 0)
        return empty;

      const selectedTask = GetTaskById(doc, taskId ?? '');
      if (!selectedTask || !selectedTask.taskContentId) return empty;

      const selectedTaskContent = GetTaskContentById(
        state.gameDocument,
        selectedTask.taskContentId
      );
      if (!selectedTaskContent || !selectedTaskContent.forms) return empty;

      const formId = (argumentAssetId as string[]).at(0) ?? '';
      const answerIds = (argumentAssetId as string[]).slice(1);

      const selectedForm = selectedTaskContent.forms.find(
        (f) => f.id === formId
      );
      if (!selectedForm || answerIds.length === 0 || !selectedForm.answers)
        return empty;

      const answers = selectedForm.answers
        .map((a) => a.answerResId)
        .filter((a) => answerIds.includes(a))
        .map((a) => `"${GetResourceValue(doc, a)}"`);

      if (answers.length === 0) return empty;
      if (answers.length === 1) return `the ${answers[0]} answer`;

      return `any of the ${answers.join(', ')} answers`;
    };

    const buildClause = (
      s: {
        identifier?: string;
        condition?: string;
        operation?: string;
        argumentAssetId?: unknown;
        argumentQuantity?: number;
      },
      taskId: string | undefined
    ) => {
      if (!s.identifier || !s.condition || !s.operation) return '';

      const qty = s.argumentQuantity;

      if (s.identifier === 'score') {
        if (s.operation !== 'greaterOrEqual' && s.operation !== 'less')
          return '';
        const isGte =
          (s.operation === 'greaterOrEqual') !== (s.condition === 'isNot');
        return `the score ${isGte ? '≥' : '<'} ${qty}`;
      }

      const negated = s.condition === 'doesNot';
      const assetId = (s.argumentAssetId as string) ?? '';
      const subject = `the ${s.identifier}`;
      const atLeast = negated ? 'fewer than' : 'at least';

      switch (s.operation) {
        case 'haveItem': {
          const itemName = GetResourceValue(
            doc,
            GetInventoryById(doc, assetId)?.titleResId ?? ''
          );
          return `${subject} has ${atLeast} ${qty} "${itemName}"`;
        }
        case 'haveScore':
          return `${subject} has ${atLeast} ${plural(qty, 'point')}`;
        case 'haveRole': {
          const titleName = GetResourceValue(
            doc,
            GetTitleById(doc, assetId)?.titleResId ?? ''
          );
          return `${subject} ${negated ? 'does not have' : 'has'} the "${titleName}" title`;
        }
        case 'chooseAnswer':
        case 'choosesAnswer': {
          const choose = negated ? 'does not choose' : 'chooses';
          return `${subject} ${choose} ${buildAnswerPhrase(
            s.argumentAssetId,
            taskId
          )}`;
        }
        default:
          return '';
      }
    };

    const buildActions = (steps: AlgorithmStep[] | undefined) =>
      (steps ?? [])
        .map((s) => (s.identifier ? buildStepOperation(s, doc) : ''))
        .filter(Boolean)
        .map(lowerFirst)
        .join(', and ');

    const first = buildClause(step, step.taskId);
    if (!first) return [];

    const compounds = (step.ifCompoundStatements ?? [])
      .map((c) => {
        const clause = buildClause(c, step.taskId);
        return clause ? `${c.type} ${clause}` : '';
      })
      .filter(Boolean);

    const thenText = buildActions(step.ifSteps as AlgorithmStep[]);
    const elseText = buildActions(step.elseSteps as AlgorithmStep[]);

    return [
      ['If ' + first, ...compounds].join(', '),
      thenText && `Then ${thenText}`,
      elseText && `Else ${elseText}`
    ].filter(Boolean) as string[];
  }, [
    step.taskId,
    step.ifSteps,
    step.elseSteps,
    step.condition,
    step.operation,
    step.identifier,
    state.gameDocument,
    step.argumentAssetId,
    step.argumentQuantity,
    step.ifCompoundStatements
  ]);

  return (
    <span
      className={'collapsed-text'}
      style={{ fontSize: '20px', lineHeight: '28px' }}>
      {lines.map((line, i) => (
        <React.Fragment key={i}>{line}. </React.Fragment>
      ))}
    </span>
  );
};
